import { toUserMessage } from '../../lib/errors.js';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonLabel,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { checkmarkOutline, timeOutline } from 'ionicons/icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useEvents, useMasters } from '../../lib/data/queries.js';
import { useOperationalIdentity } from '../../lib/auth/useOperationalIdentity.js';
import { isAssignedToOperationalIdentity } from '../../lib/auth/operationalAssignment.js';
import { useOperationalCheckEvent } from '../../lib/haccp/useOperationalCheckEvent.js';
import { CLEANING_FREQ_HOURS, classifyCleaning } from '../../lib/haccp/overdue.js';
import {
  LATE_ENTRY_LOOKBACK_DAYS,
  combineLocalDateTime,
  dueDayKeyIfOverdue,
  formatDayHeading,
  lateEntryMarker,
  localDateKey,
  localDateKeyOf,
  mergeMissingDays,
  missingDailyDays,
  pastDayKeys,
  type MissingDay,
} from '../../lib/haccp/lateEntry.js';
import { monthlyDueState, weeklyDueState } from '../../lib/haccp/cassettoSchedule.js';
import { LateTimeModal } from '../../components/haccp/LateTimeModal.js';
import { EmptyHint, palette } from '../../components/design.js';

interface CleaningSchedule {
  id: string;
  title: string;
  frequency: string;
  business_id: string;
  responsible_role: string | null;
  equipment_id: string | null;
  product_id: string | null;
  product_ids?: string[] | null;
  created_at?: string | null;
  assigned_personnel_ids?: string[] | null;
  requires_photo?: boolean;
}

interface CleaningEvent {
  payload: { schedule_id: string };
  client_ts: string;
}

type Bucket = 'oggi' | 'giorni';

// Cap di fetch eventi: alto perché i cleaning_done sono pochi al giorno; oltre
// il cap NON dichiariamo "mancante" un giorno che non possiamo conoscere.
const EVENTS_FETCH_CAP = 1000;

interface RecoverTarget {
  dayKey: string;
  schedules: CleaningSchedule[];
  label: string;
}

interface BusinessCfgRow {
  id: string;
  cleaning_weekly_weekday?: number | null;
  cleaning_monthly_day?: number | null;
  archived_at?: string | null;
}

export function CleaningTodoPage(): JSX.Element {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const { data: schedules = [] } = useMasters<CleaningSchedule>('cleaning_schedules');
  const { data: events = [] } = useEvents('cleaning_done', EVENTS_FETCH_CAP);
  const { data: businesses = [] } = useMasters<BusinessCfgRow>('businesses');
  const identity = useOperationalIdentity();
  const operationalEvent = useOperationalCheckEvent();
  const assignedSchedules = useMemo(() => schedules.filter((schedule) => isAssignedToOperationalIdentity(
    schedule.assigned_personnel_ids,
    identity.personnelId,
    identity.canManageOperationalWork,
  )), [identity.canManageOperationalWork, identity.personnelId, schedules]);
  const [bucket, setBucket] = useState<Bucket>('oggi');
  const [toast, setToast] = useState<string | null>(null);
  const [recover, setRecover] = useState<RecoverTarget | null>(null);

  // Ultima esecuzione + insieme dei giorni (locali) eseguiti, per schedule.
  const { lastDone, doneDayKeysById, earliestTrustedMs } = useMemo(() => {
    const last = new Map<string, number>();
    const doneDays = new Map<string, Set<string>>();
    let earliest = Number.POSITIVE_INFINITY;
    for (const e of events as CleaningEvent[]) {
      const ms = Date.parse(e.client_ts);
      if (Number.isNaN(ms)) continue;
      if (ms < earliest) earliest = ms;
      const sid = e.payload.schedule_id;
      const cur = last.get(sid);
      if (cur === undefined || ms > cur) last.set(sid, ms);
      const dk = localDateKeyOf(e.client_ts);
      if (dk) {
        const set = doneDays.get(sid) ?? new Set<string>();
        set.add(dk);
        doneDays.set(sid, set);
      }
    }
    const truncated = events.length >= EVENTS_FETCH_CAP;
    return {
      lastDone: last,
      doneDayKeysById: doneDays,
      earliestTrustedMs: truncated && Number.isFinite(earliest) ? earliest : null,
    };
  }, [events]);

  const createdAtMsById = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of assignedSchedules) {
      const ms = s.created_at ? Date.parse(s.created_at) : NaN;
      if (!Number.isNaN(ms)) m.set(s.id, ms);
    }
    return m;
  }, [assignedSchedules]);

  // ── Tab "Oggi": voci scadute entro le 24h (da fare oggi). ──────────────
  const todayList = useMemo(
    () => assignedSchedules.filter((s) => classifyCleaning(s, lastDone.get(s.id)) !== null),
    [assignedSchedules, lastDone],
  );

  // T-341: config per-sede dei giorni di scadenza (settimanale/mensile).
  const activeBusiness = businesses.find(
    (b) => b.id === profile?.active_business_id && !b.archived_at,
  ) ?? null;
  const weeklyWeekday = activeBusiness?.cleaning_weekly_weekday ?? null;
  const monthlyDay = activeBusiness?.cleaning_monthly_day ?? null;

  // ── Tab "Giorni scorsi": voci non eseguite, raggruppate per giorno. ────
  const missingDays: MissingDay<CleaningSchedule>[] = useMemo(() => {
    const nowMs = Date.now();
    const now = new Date(nowMs);
    const dayKeys = pastDayKeys(nowMs, LATE_ENTRY_LOOKBACK_DAYS);
    const windowStart = dayKeys[dayKeys.length - 1];
    const yesterday = dayKeys[0];

    // Giornaliere: una riga per ogni giorno non eseguito.
    const daily = assignedSchedules.filter((s) => s.frequency === 'giornaliera');
    const dailyMissing = missingDailyDays(daily, doneDayKeysById, {
      todayMs: nowMs,
      lookbackDays: LATE_ENTRY_LOOKBACK_DAYS,
      createdAtMsById,
      earliestTrustedMs,
    });

    // T-341: per settimanale/mensile, se la sede ha configurato il giorno, la
    // scadenza è l'ultima occorrenza di quel giorno (lastDue) e la voce è
    // arretrata se non eseguita da allora. Altrimenti fallback T-333
    // (lastDone+frequenza). Le altre cadenze (trimestrale/…) restano sul fallback.
    const weeklyState = weeklyDueState(now, weeklyWeekday);
    const monthlyState = monthlyDueState(now, monthlyDay);
    function configuredDueKey(s: CleaningSchedule): string | null {
      const st = s.frequency === 'settimanale' ? weeklyState
        : s.frequency === 'mensile' ? monthlyState
        : null;
      if (!st || !st.configured || !st.lastDue) return null;
      const since = st.lastDue.getTime();
      const doneSince = (lastDone.get(s.id) ?? -Infinity) >= since;
      return doneSince ? null : localDateKey(st.lastDue);
    }

    // Non-giornaliere scadute: attribuite al loro giorno di scadenza (entro la finestra).
    const recurringByDay = new Map<string, CleaningSchedule[]>();
    for (const s of assignedSchedules) {
      if (s.frequency === 'giornaliera') continue;
      const freqHours = CLEANING_FREQ_HOURS[s.frequency] ?? Number.POSITIVE_INFINITY;
      const dueKey = configuredDueKey(s) ?? dueDayKeyIfOverdue({
        lastDoneMs: lastDone.get(s.id),
        createdAtMs: createdAtMsById.get(s.id),
        freqHours,
        nowMs,
      });
      if (!dueKey) continue;
      if (dueKey < windowStart || dueKey > yesterday) continue; // fuori finestra
      const arr = recurringByDay.get(dueKey) ?? [];
      arr.push(s);
      recurringByDay.set(dueKey, arr);
    }
    const recurringMissing: MissingDay<CleaningSchedule>[] = [...recurringByDay.entries()].map(
      ([date, items]) => ({ date, items }),
    );

    return mergeMissingDays(dailyMissing, recurringMissing);
  }, [assignedSchedules, doneDayKeysById, createdAtMsById, lastDone, earliestTrustedMs, weeklyWeekday, monthlyDay]);

  // Registrazione ODIERNA (tab Oggi): client_ts = now, nessun marker.
  async function markDone(s: CleaningSchedule): Promise<void> {
    if (!user) return;
    try {
      const result = await operationalEvent.record({
        eventKind: 'cleaning_done',
        evidenceKind: 'cleaning',
        targetId: s.id,
        businessId: s.business_id,
        requiresPhoto: s.requires_photo === true,
        payload: {
          schedule_id: s.id,
          equipment_id: s.equipment_id,
          product_id: (s.product_ids && s.product_ids[0]) ?? s.product_id ?? null,
          performed_by_user_id: user.id,
          operator_name: profile?.full_name?.trim() || user.email || 'Operatore autenticato',
        },
      });
      if (result === 'cancelled') return;
      setToast(t('haccp.cleaningDone'));
    } catch (err: unknown) {
      setToast(toUserMessage(err));
    }
  }

  // Recupero POSTUMO (tab Giorni scorsi): client_ts = giorno + ora dichiarata,
  // payload con marker recorded_late + operational_date.
  async function commitRecover(hhmm: string): Promise<void> {
    if (!user || !recover) return;
    const { dayKey, schedules: list } = recover;
    let clientTs: string;
    try {
      clientTs = combineLocalDateTime(dayKey, hhmm);
    } catch {
      setToast('Ora non valida');
      return;
    }
    const marker = lateEntryMarker(dayKey, hhmm);
    try {
      for (const s of list) {
        const result = await operationalEvent.record({
          eventKind: 'cleaning_done',
          evidenceKind: 'cleaning',
          targetId: s.id,
          businessId: s.business_id,
          requiresPhoto: s.requires_photo === true,
          clientTs,
          payload: {
            schedule_id: s.id,
            equipment_id: s.equipment_id,
            product_id: (s.product_ids && s.product_ids[0]) ?? s.product_id ?? null,
            performed_by_user_id: user.id,
            operator_name: profile?.full_name?.trim() || user.email || 'Operatore autenticato',
            ...marker,
          },
        });
        if (result === 'cancelled') break;
      }
      setToast(
        list.length === 1
          ? `Recuperato: ${list[0].title} (${formatDayHeading(dayKey)})`
          : `${list.length} voci recuperate · ${formatDayHeading(dayKey)}`,
      );
    } catch (err: unknown) {
      setToast(toUserMessage(err));
    } finally {
      setRecover(null);
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/haccp/cleaning" />
          </IonButtons>
          <IonTitle>Da fare</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={bucket} onIonChange={(ev) => setBucket((ev.detail.value as Bucket) ?? 'oggi')}>
            <IonSegmentButton value="oggi">
              <IonLabel>Oggi</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="giorni">
              <IonLabel>Giorni scorsi</IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': palette.bg } as React.CSSProperties}>
        <div style={{ padding: '12px 16px 24px' }}>
          {bucket === 'oggi' ? (
            todayList.length === 0 ? (
              <div style={{ background: palette.card, padding: 16, borderRadius: 14 }}>
                <EmptyHint>{t('common.empty')}</EmptyHint>
              </div>
            ) : (
              todayList.map((s) => (
                <div
                  key={s.id}
                  onClick={() => void markDone(s)}
                  style={rowStyle(palette.textFaint)}
                >
                  <Checkbox />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: palette.text }}>{s.title}</div>
                    <div style={{ fontSize: 12, color: palette.textFaint, marginTop: 2 }}>
                      {s.frequency}
                      {s.responsible_role ? ` · ${s.responsible_role}` : ''}
                    </div>
                  </div>
                </div>
              ))
            )
          ) : missingDays.length === 0 ? (
            <div style={{ background: palette.card, padding: 16, borderRadius: 14 }}>
              <EmptyHint>Nessuna pulizia arretrata da recuperare 🎉</EmptyHint>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: palette.textFaint, margin: '2px 2px 10px', lineHeight: 1.4 }}>
                Pulizie non registrate dei giorni scorsi. Tocca una voce per
                recuperarla: ti verrà chiesto <b>a che ora</b> l'hai eseguita,
                così il registro resta veritiero.
              </div>
              {missingDays.map((day) => (
                <div
                  key={day.date}
                  style={{
                    background: palette.card,
                    borderRadius: 16,
                    boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 8px rgba(15,23,42,.06)',
                    margin: '12px 0',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      padding: '12px 14px 8px',
                    }}
                  >
                    <span style={{ fontSize: 15, fontWeight: 700, color: palette.text, textTransform: 'capitalize' }}>
                      {formatDayHeading(day.date)}
                    </span>
                    <span style={{ fontSize: 12, color: palette.textFaint }}>{day.items.length} da recuperare</span>
                  </div>
                  <div style={{ padding: '0 14px' }}>
                    {day.items.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() =>
                          setRecover({ dayKey: day.date, schedules: [s], label: s.title })
                        }
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          background: 'transparent',
                          border: 'none',
                          borderBottom: `1px solid ${palette.border}`,
                          padding: '10px 0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          cursor: 'pointer',
                        }}
                      >
                        <Checkbox />
                        <span style={{ flex: 1, fontSize: 14, color: palette.text }}>{s.title}</span>
                        <IonIcon icon={timeOutline} style={{ fontSize: 18, color: palette.accent }} />
                      </button>
                    ))}
                  </div>
                  {day.items.length > 1 && (
                    <div style={{ padding: '10px 14px 14px' }}>
                      <button
                        type="button"
                        onClick={() =>
                          setRecover({
                            dayKey: day.date,
                            schedules: day.items,
                            label: `${day.items.length} voci`,
                          })
                        }
                        style={{
                          width: '100%',
                          background: palette.accent,
                          color: '#fff',
                          border: 'none',
                          borderRadius: 10,
                          padding: '11px 8px',
                          fontSize: 13,
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6,
                          cursor: 'pointer',
                        }}
                      >
                        <IonIcon icon={checkmarkOutline} /> Recupera tutte ({day.items.length})
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>

        <LateTimeModal
          isOpen={recover !== null}
          title="Recupero pulizia"
          dayKey={recover?.dayKey ?? null}
          subtitle={recover?.label}
          onConfirm={(hhmm) => void commitRecover(hhmm)}
          onDismiss={() => setRecover(null)}
        />

        <IonToast
          isOpen={!!toast}
          message={toast ?? ''}
          duration={1500}
          onDidDismiss={() => setToast(null)}
        />
      </IonContent>
    </IonPage>
  );
}

function rowStyle(border: string): React.CSSProperties {
  return {
    background: palette.card,
    borderRadius: 14,
    borderLeft: `4px solid ${border}`,
    boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
    margin: '10px 0',
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    cursor: 'pointer',
  };
}

function Checkbox(): JSX.Element {
  return (
    <div
      style={{
        width: 24,
        height: 24,
        borderRadius: 8,
        border: `2px solid ${palette.borderStrong}`,
        background: palette.card,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <IonIcon icon={checkmarkOutline} style={{ color: 'transparent', fontSize: 16 }} />
    </div>
  );
}
